import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Edit, Trash2, Save, RefreshCw, Home, Globe } from 'lucide-react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';

const GeolocationAdminPanel = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    requireGPS: true,
    allowOutsideRadius: true,
    defaultRadius: 100,
    workingHours: {
      start: '08:00',
      end: '18:00'
    }
  });
  
  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius: 100,
    isActive: true,
    isMainOffice: false
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // 🚀 CORRECTION: Fonctions de gestion d'état optimisées pour éviter le re-render
  // eslint-disable-next-line no-unused-vars
  const _updateNewLocation = useCallback((field, value) => {
    setNewLocation(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars
  const _updateEditingLocation = useCallback((field, value) => {
    setEditingLocation(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateSettings = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateWorkingHours = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [field]: value
      }
    }));
  }, []);

  // Chargement initial
  useEffect(() => {
    loadSettings();
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/geolocation-settings`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || settings);
      } else {
        console.warn('⚠️ Paramètres non chargés, utilisation des valeurs par défaut');
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/locations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      } else {
        console.error('❌ Erreur chargement lieux:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur chargement lieux:', error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _getCurrentPosition = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          if (editingLocation) {
            setEditingLocation(prev => ({
              ...prev,
              latitude: lat,
              longitude: lng
            }));
          } else {
            setNewLocation(prev => ({
              ...prev,
              latitude: lat,
              longitude: lng
            }));
          }
          setLoading(false);
          setMessage(`✅ ${t('gps.position_detected')}`);
          setTimeout(() => setMessage(''), 3000);
        },
        (error) => {
          setLoading(false);
          setMessage(`❌ ${t('gps.error_geolocation_unsupported')}`);
          setTimeout(() => setMessage(''), 3000);
        }
      );
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/attendance/geolocation-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage(`✅ ${t('gps.success_settings_saved')}`);
      } else {
        setMessage(`❌ ${data.error || t('gps.error_save')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error);
      setMessage(`❌ ${t('gps.error_save_settings')}`);
    }
    setLoading(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const saveLocation = async (locationData) => {
    setLoading(true);
    try {
      const parsedRadius = Number.parseInt(locationData?.radius, 10);
      const safeRadius = Number.isFinite(parsedRadius) ? parsedRadius : 100;
      const payload = {
        ...locationData,
        radius: safeRadius
      };

      const url = locationData.id ? 
        `${API_BASE}/api/attendance/locations/${locationData.id}` : 
        `${API_BASE}/api/attendance/locations`;
      
      const response = await fetch(url, {
        method: locationData.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage(`✅ ${locationData.id ? t('gps.success_location_updated') : t('gps.success_location_added')}`);
        loadLocations();
        setShowAddForm(false);
        setEditingLocation(null);
        setNewLocation({
          name: '', address: '', latitude: '', longitude: '', 
          radius: 100, isActive: true, isMainOffice: false
        });
      } else {
        setMessage(`❌ ${data.error || t('gps.error_save')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde lieu:', error);
      setMessage(`❌ ${t('gps.error_save_location')}`);
    }
    setLoading(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const deleteLocation = async (id) => {
    if (window.confirm(t('gps.delete_confirm'))) {
      try {
        const response = await fetch(`${API_BASE}/api/attendance/locations/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        
        const data = await response.json();
        if (data.success) {
          setMessage(`✅ ${t('gps.success_location_deleted')}`);
          loadLocations();
        } else {
          setMessage(`❌ ${data.error || t('gps.error_save')}`);
        }
      } catch (error) {
        console.error('Erreur suppression lieu:', error);
        setMessage(`❌ ${t('gps.error_delete_location')}`);
      }
      setTimeout(() => setMessage(''), 3000);
    }
  };

  // 🚀 CORRECTION CRITIQUE: Composant LocationForm complètement isolé
  const LocationForm = React.memo(({ location, onSave, onCancel, isEditing, t }) => {
    // État local COMPLÈTEMENT isolé
    const [localLocation, setLocalLocation] = useState(location);
    const [localLoading, setLocalLoading] = useState(false);
    const [localMessage, setLocalMessage] = useState('');

    // Synchroniser l'état local avec les props UNE SEULE FOIS
    useEffect(() => {
      setLocalLocation(location);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.id]); // Seulement si l'ID change

    // Fonction de gestion stable
    const handleFieldChange = useCallback((field, value) => {
      setLocalLocation(prev => ({
        ...prev,
        [field]: value
      }));
    }, []);

    // Fonction de sauvegarde qui transmet l'état local
    const handleSave = useCallback(() => {
      onSave(localLocation);
    }, [localLocation, onSave]);

    // 🚀 CORRECTION: Fonction GPS complètement isolée
    const handleGPSDetection = useCallback(() => {
      setLocalLoading(true);
      setLocalMessage(`🛰️ ${t('gps.detecting')}`);
      
      if (!navigator.geolocation) {
        setLocalLoading(false);
        setLocalMessage(`❌ ${t('gps.error_geolocation_unsupported')}`);
        setTimeout(() => setLocalMessage(''), 3000);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude.toFixed(6);
          const lng = position.coords.longitude.toFixed(6);
          
          console.log('🛰️ GPS OK:', { lat, lng });
          
          // Mettre à jour SEULEMENT l'état local
          setLocalLocation(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
          }));
          
          setLocalLoading(false);
          setLocalMessage(`✅ ${(t('gps.position_detected_m') || 'Position detected ({{meters}}m)').replace('{{meters}}', Math.round(position.coords.accuracy))}`);
          setTimeout(() => setLocalMessage(''), 4000);
        },
        (error) => {
          console.error('❌ GPS Error:', error);
          setLocalLoading(false);
          
          let errorMessage = '';
          switch(error.code) {
            case 1: errorMessage = `❌ ${t('gps.error_geolocation_denied')}`; break;
            case 2: errorMessage = `❌ ${t('gps.error_position_unavailable')}`; break;
            case 3: errorMessage = `❌ ${t('gps.error_timeout')}`; break;
            default: errorMessage = `❌ ${t('gps.error_gps_unknown')}`; break;
          }
          
          setLocalMessage(errorMessage);
          setTimeout(() => setLocalMessage(''), 4000);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        padding: '20px',
        borderRadius: '16px',
        border: '2px solid rgba(255, 255, 255, 0.1)',
        marginBottom: '20px'
      }}>
        <h4 style={{
          color: '#ffffff',
          marginBottom: '15px',
          fontSize: '18px',
          fontWeight: 700
        }}>
          {isEditing ? `✏️ ${t('gps.edit_location')}` : `➕ ${t('gps.new_location')}`}
        </h4>

        {/* Message local isolé */}
        {localMessage && (
          <div style={{
            background: localMessage.includes('✅')
              ? 'rgba(16, 185, 129, 0.15)'
              : localMessage.includes('🛰️')
              ? 'rgba(59, 130, 246, 0.15)'
              : 'rgba(239, 68, 68, 0.15)',
            color: localMessage.includes('✅')
              ? '#34d399'
              : localMessage.includes('🛰️')
              ? '#93c5fd'
              : '#fca5a5',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: 600,
            border: `1px solid ${localMessage.includes('✅') ? 'rgba(16, 185, 129, 0.4)' : localMessage.includes('🛰️') ? 'rgba(59, 130, 246, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
            backdropFilter: 'blur(10px)'
          }}>
            {localMessage}
          </div>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              🏢 {t('gps.location_name')}
            </label>
            <input
              type="text"
              value={localLocation.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder={t('gps.location_name_placeholder')}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              📍 {t('gps.full_address')}
            </label>
            <input
              type="text"
              value={localLocation.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder={t('gps.full_address_placeholder')}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              🌐 {t('gps.latitude')}
            </label>
            <input
              type="text"
              value={localLocation.latitude || ''}
              onChange={(e) => handleFieldChange('latitude', e.target.value)}
              placeholder="48.8566"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                fontFamily: 'monospace',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              🌐 {t('gps.longitude')}
            </label>
            <input
              type="text"
              value={localLocation.longitude || ''}
              onChange={(e) => handleFieldChange('longitude', e.target.value)}
              placeholder="2.3522"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                fontFamily: 'monospace',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              📏 {t('gps.radius_m')}
            </label>
            <input
              type="number"
              value={localLocation.radius ?? 100}
              onChange={(e) => {
                // Keep typing flexible; sanitize numeric value on save.
                const nextValue = e.target.value;
                handleFieldChange('radius', nextValue === '' ? '' : Number.parseInt(nextValue, 10));
              }}
              min="10"
              max="5000"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '20px', marginTop: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={localLocation.isActive}
              onChange={(e) => handleFieldChange('isActive', e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            ✅ {t('gps.location_active')}
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={localLocation.isMainOffice}
              onChange={(e) => handleFieldChange('isMainOffice', e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            🏢 {t('gps.main_office')}
          </label>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleGPSDetection}
            disabled={localLoading}
            style={{
              background: localLoading
                ? 'rgba(107, 114, 128, 0.5)'
                : 'linear-gradient(135deg, #06b6d4, #0891b2)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: localLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              boxShadow: localLoading ? 'none' : '0 4px 12px rgba(6, 182, 212, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!localLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!localLoading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(6, 182, 212, 0.3)';
              }
            }}
          >
            <MapPin size={18} />
            {localLoading ? t('gps.detecting') : t('gps.detect_position')}
          </button>

          <button
            onClick={handleSave}
            disabled={localLoading || !localLocation.name || !localLocation.address}
            style={{
              background: (!localLocation.name || !localLocation.address || localLoading)
                ? 'rgba(107, 114, 128, 0.5)'
                : 'linear-gradient(135deg, #10b981, #059669)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: (!localLocation.name || !localLocation.address || localLoading) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              boxShadow: (!localLocation.name || !localLocation.address || localLoading)
                ? 'none'
                : '0 4px 12px rgba(16, 185, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!localLoading && localLocation.name && localLocation.address) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!localLoading && localLocation.name && localLocation.address) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }
            }}
          >
            <Save size={18} />
            {t('gps.save')}
          </button>

          <button
            onClick={onCancel}
            style={{
              background: 'rgba(107, 114, 128, 0.3)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(107, 114, 128, 0.4)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(107, 114, 128, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {t('gps.cancel')}
          </button>
        </div>
      </div>
    );
  });

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('gps.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Globe size={32} />
            {t('gps.title')}
          </div>
          <button
            onClick={() => {
              loadSettings();
              loadLocations();
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              padding: '10px 16px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
              fontSize: '14px',
              fontWeight: 600
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <RefreshCw size={18} />
            {t('gps.refresh')}
          </button>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('gps.subtitle')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="gps-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>

      {/* Message de statut */}
      {message && (
        <div style={{
          background: message.includes('✅')
            ? 'rgba(16, 185, 129, 0.15)'
            : 'rgba(239, 68, 68, 0.15)',
          color: message.includes('✅') ? '#34d399' : '#fca5a5',
          padding: '16px 24px',
          borderRadius: '12px',
          marginBottom: '24px',
          border: `1px solid ${message.includes('✅') ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
          backdropFilter: 'blur(10px)',
          fontWeight: 600
        }}>
          {message}
        </div>
      )}

      {/* Paramètres généraux */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '24px',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        marginBottom: '24px'
      }}>
        <h2 style={{
          color: '#ffffff',
          marginBottom: '24px',
          fontSize: '20px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <MapPin size={20} />
          {t('gps.general_params')}
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '12px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              📏 {t('gps.default_radius_m')}
            </label>
            <input
              type="number"
              value={settings.defaultRadius}
              onChange={(e) => updateSettings('defaultRadius', parseInt(e.target.value) || 100)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '12px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              🕐 {t('gps.start_time')}
            </label>
            <input
              type="time"
              value={settings.workingHours.start}
              onChange={(e) => updateWorkingHours('start', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontWeight: 600,
              marginBottom: '12px',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              🕕 {t('gps.end_time')}
            </label>
            <input
              type="time"
              value={settings.workingHours.end}
              onChange={(e) => updateWorkingHours('end', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                boxSizing: 'border-box',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '24px', marginTop: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={settings.requireGPS}
              onChange={(e) => updateSettings('requireGPS', e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            🛰️ {t('gps.require_geolocation')}
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={settings.allowOutsideRadius}
              onChange={(e) => updateSettings('allowOutsideRadius', e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#3b82f6'
              }}
            />
            ⚠️ {t('gps.allow_outside_alert')}
          </label>
        </div>

        <button
          onClick={saveSettings}
          disabled={loading}
          style={{
            background: loading
              ? 'rgba(107, 114, 128, 0.5)'
              : 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: '#ffffff',
            border: 'none',
            padding: '14px 28px',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            boxShadow: loading ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.3)'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
            }
          }}
        >
          <Save size={18} />
          {loading ? t('gps.saving') : t('gps.save_general')}
        </button>
      </div>

      {/* Gestion des lieux */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '24px',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            color: '#ffffff',
            margin: 0,
            fontSize: '20px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <MapPin size={20} />
            {t('gps.work_locations')} ({locations.length})
          </h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
          >
            <Plus size={18} />
            {t('gps.add_location')}
          </button>
        </div>

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <LocationForm
            location={newLocation}
            onSave={saveLocation}
            onCancel={() => setShowAddForm(false)}
            isEditing={false}
            t={t}
          />
        )}

        {/* Formulaire d'édition */}
        {editingLocation && (
          <LocationForm
            location={editingLocation}
            onSave={saveLocation}
            onCancel={() => setEditingLocation(null)}
            isEditing={true}
            t={t}
          />
        )}

        {/* Liste des lieux existants */}
        {locations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'rgba(255, 255, 255, 0.7)',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <MapPin size={48} style={{ color: 'rgba(255, 255, 255, 0.4)', marginBottom: '16px' }} />
            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: '#ffffff' }}>
              {t('gps.no_locations_configured')}
            </div>
            <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
              {t('gps.add_first_location_hint')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {locations.map(location => (
              <div
                key={location.id}
                style={{
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '20px',
                  background: location.isActive
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = location.isActive
                    ? 'rgba(16, 185, 129, 0.15)'
                    : 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = location.isActive
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h3 style={{ color: '#ffffff', margin: 0, fontSize: '18px', fontWeight: 700 }}>
                        {location.isMainOffice ? '🏢' : '📍'} {location.name}
                      </h3>
                      {!location.isActive && (
                        <span style={{
                          background: 'rgba(107, 114, 128, 0.3)',
                          color: 'rgba(255, 255, 255, 0.8)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {t('gps.inactive')}
                        </span>
                      )}
                      {location.isMainOffice && (
                        <span style={{
                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          color: '#ffffff',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                        }}>
                          {t('gps.main_office_short')}
                        </span>
                      )}
                    </div>
                    
                    <div style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px', fontSize: '14px' }}>
                      📍 {location.address}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', flexWrap: 'wrap' }}>
                      <span>🌐 {location.latitude}, {location.longitude}</span>
                      <span>📏 {t('gps.radius_value', { value: location.radius })}</span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setEditingLocation(location)}
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                      }}
                      title={t('gps.edit_title')}
                    >
                      <Edit size={18} />
                    </button>
                    
                    <button
                      onClick={() => deleteLocation(location.id)}
                      disabled={loading}
                      style={{
                        background: loading
                          ? 'rgba(107, 114, 128, 0.5)'
                          : 'linear-gradient(135deg, #ef4444, #dc2626)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '10px',
                        borderRadius: '12px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        boxShadow: loading ? 'none' : '0 2px 8px rgba(239, 68, 68, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                        }
                      }}
                      title={t('gps.delete_title')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .gps-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .gps-content {
            padding: 180px 60px 60px !important;
          }
        }
        .gps-content {
          animation: slideUp 0.4s ease-out;
        }
        input[type="time"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default GeolocationAdminPanel;